// Autosave during a single-player match (pacing spec §4 A3). Saves every
// AUTOSAVE_EVERY_TICKS of game time, and whenever the tab is hidden or the
// page is closed, so shutting the laptop never loses more than a few
// minutes. Honours the Settings → Gameplay "Autosave" toggle.

import { useCallback, useEffect, useRef } from 'react';
import { formatGameTime } from '../sim/gameTime.js';
import { writeAutosave } from './autosaveStore.js';

/** 5 minutes of game time at the default 10 ticks/s. */
export const AUTOSAVE_EVERY_TICKS = 3000;
/** Never save more often than this (real time), e.g. rapid tab switching. */
export const AUTOSAVE_MIN_GAP_MS = 20_000;

export interface AutosaveOptions {
  readonly enabled: boolean;
  readonly tick: number;
  readonly saveGame: () => string;
  /** Injected for tests. */
  readonly write?: typeof writeAutosave;
  readonly now?: () => number;
}

export function useAutosave({
  enabled,
  tick,
  saveGame,
  write = writeAutosave,
  now = Date.now,
}: AutosaveOptions): void {
  const lastTickRef = useRef<number | null>(null);
  const lastAtRef = useRef(0);
  const tickRef = useRef(tick);
  tickRef.current = tick;

  const save = useCallback(
    (force: boolean) => {
      const t = tickRef.current;
      if (lastTickRef.current !== null && t === lastTickRef.current) return; // nothing new
      if (!force && now() - lastAtRef.current < AUTOSAVE_MIN_GAP_MS) return;
      let json = '';
      try {
        json = saveGame();
      } catch {
        return;
      }
      if (!json) return;
      lastTickRef.current = t;
      lastAtRef.current = now();
      void write({ savedAt: now(), tick: t, label: formatGameTime(t), json });
    },
    [saveGame, write, now],
  );

  // Periodic: every AUTOSAVE_EVERY_TICKS of game time.
  useEffect(() => {
    if (!enabled) return;
    const last = lastTickRef.current;
    if (last === null || tick < last) {
      // First tick seen, or a new match / loaded save: start counting here.
      lastTickRef.current = tick;
      return;
    }
    if (tick - last >= AUTOSAVE_EVERY_TICKS) save(false);
  }, [enabled, tick, save]);

  // Leaving: tab hidden or page closing.
  useEffect(() => {
    if (!enabled) return;
    const onHide = () => {
      if (document.visibilityState === 'hidden') save(true);
    };
    const onPageHide = () => save(true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [enabled, save]);
}
